import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import * as commitmentsApi from '../api/commitmentsApi';
import type {
  CloseCommitmentInput,
  CloseOutcome,
  CommitmentScope,
  CommitmentSummary,
  CreateCommitmentInput,
  Dashboard,
  IterationPreview,
  IterationStarted,
  ReopenCommitmentInput,
  ReopenOutcome,
  TeamWorkload,
} from '../model/types';

export const commitmentKeys = {
  all: ['commitments'] as const,
  forElement: (elementId: number, scope: CommitmentScope) =>
    ['commitments', 'element', elementId, scope] as const,
  detail: (id: number) => ['commitments', 'detail', id] as const,
  iterationPreview: (elementId: number) => ['commitments', 'iteration-preview', elementId] as const,
  dashboard: () => ['commitments', 'dashboard'] as const,
  teamWorkload: () => ['commitments', 'team-workload'] as const,
};

/**
 * Unieważnienie po każdej zmianie stanu zobowiązania.
 *
 * ⚠ Zasięg jest **szerszy niż ruszony element** i to jest celowe. Zamknięcie budzi
 * albo osierocia zobowiązania na **cudzych elementach** (decyzje 21–22), więc liczby
 * przy węzłach drzewka zmieniają się w miejscach, których ta mutacja nie dotykała
 * wprost. Unieważnienie po samym gospodarzu zostawiałoby na ekranie liczby, o których
 * już wiadomo, że są nieprawdziwe.
 *
 * `elements` unieważniamy prefiksem, bo klucze wsadowe (`work-counts`, `owners`,
 * `rollup`) niosą całą listę identyfikatorów — nie da się trafić w ten jeden.
 *
 * ⭐⭐ Od decyzji 173 w tym zbiorze jest też **`constellation`**, i to nie jest dołożenie „przy
 * okazji": liczby pracy jadą teraz RAZEM z kształtem drzewka, więc odświeżenie liczb JEST
 * odświeżeniem tej odpowiedzi. Pominięcie jej zostawiłoby na ekranie liczby, o których już
 * wiadomo, że są nieprawdziwe — czyli dokładnie to, przed czym broni akapit wyżej.
 *
 * ⚠ To NIE przestawia wierszy. Wybór widocznej siódemki stoi na migawce ciepła (decyzja 172),
 * więc odświeżenie zmienia liczby, a nie kolejność — zmierzone w przeglądarce 2026-09-06.
 *
 * 📏 Cena zmierzona przed zmianą (dev, korzeń na głębokości 1 + pięć rozwiniętych gałęzi,
 * 204 węzły): samo `usage` ważyło 18,1 KB, całe drzewko 39,3 KB. Odświeżenie po domknięciu
 * zadania jest więc ok. dwa razy droższe — a w zamian **każde rozwinięcie** jest tańsze o tyle,
 * ile ważyło osobne `usage` nad rosnącym zbiorem. Rozwijanie jest częste, domykanie rzadkie.
 */
function invalidateAfterChange(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: commitmentKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['elements', 'constellation'] });
  void queryClient.invalidateQueries({ queryKey: ['elements', 'work-counts'] });
  void queryClient.invalidateQueries({ queryKey: ['elements', 'rollup'] });
  void queryClient.invalidateQueries({ queryKey: ['elements', 'detail'] });
}

/**
 * Lista zobowiązań na elemencie (spec sek. 4, panel E1).
 *
 * `enabled` bierze się stąd, że panel bywa zamknięty — a wtedy nie ma po co pytać.
 * Historia jedzie osobnym wywołaniem z `scope: 'closed'` i włącza się dopiero po
 * rozwinięciu sekcji (decyzja 66), bo zamkniętych bywa wielokrotnie więcej niż otwartych.
 */
export function useElementCommitments(
  elementId: number | null,
  scope: CommitmentScope,
  enabled = true,
) {
  return useQuery<CommitmentSummary[]>({
    queryKey: commitmentKeys.forElement(elementId ?? 0, scope),
    queryFn: ({ signal }) => commitmentsApi.fetchForElement(elementId!, scope, signal),
    enabled: enabled && elementId !== null,
  });
}

/**
 * Tworzenie zobowiązania.
 *
 * Bez optimistic UI — tak samo jak przy tworzeniu elementu: identyfikator przychodzi
 * z serwera, a wynik i tak od razu unieważnia liczby przy węzłach drzewka.
 */
export function useCreateCommitment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCommitmentInput) => commitmentsApi.createCommitment(input),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * Zamknięcie zobowiązania — `done` albo `obsolete`.
 *
 * ⛔ **Świadomie bez optimistic UI**, mimo że szablon z sek. 4B każe go używać przy
 * mutacjach. Powód jest merytoryczny, nie techniczny: odpowiedź niesie **listę
 * obudzonych i osieroconych** zobowiązań, czyli informację, której front nie ma jak
 * przewidzieć — łańcuch Surtów liczy serwer. Optimistic UI musiałby ją zmyślić albo
 * przemilczeć, a to jest właśnie ta informacja, dla której produkt istnieje.
 */
export function useCloseCommitment() {
  const queryClient = useQueryClient();

  return useMutation<CloseOutcome, Error, CloseCommitmentInput>({
    mutationFn: ({ id, reason }) => commitmentsApi.closeCommitment(id, reason),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * Ponowne otwarcie zamkniętego zobowiązania (decyzja 22).
 *
 * Skutki uboczne bywają dwa naraz: obudzone wracają do snu, a draugry przestają nimi być.
 */
export function useReopenCommitment() {
  const queryClient = useQueryClient();

  return useMutation<ReopenOutcome, Error, ReopenCommitmentInput>({
    mutationFn: ({ id }) => commitmentsApi.reopenCommitment(id),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * Zapowiedź odcięcia iteracji (spec sek. 7) — pobierana dopiero, gdy człowiek otworzy
 * potwierdzenie. `staleTime: 0` jak przy podglądzie kampanii: pytanie brzmi „co się stanie,
 * jeśli kliknę TERAZ", a zapamiętana odpowiedź sprzed kwadransa odpowiada na inne.
 */
export function useIterationPreview(elementId: number, enabled: boolean) {
  return useQuery<IterationPreview>({
    queryKey: commitmentKeys.iterationPreview(elementId),
    queryFn: ({ signal }) => commitmentsApi.fetchIterationPreview(elementId, signal),
    enabled,
    staleTime: 0,
  });
}

/**
 * Odcięcie iteracji. ⛔ Bez optimistic UI i bez cofnięcia (decyzja 91): obudzone zadania są już
 * na cudzych pulpitach, a odpowiedź niesie listę obudzonych — czekanie na serwer jest treścią.
 */
export function useStartIteration() {
  const queryClient = useQueryClient();

  return useMutation<IterationStarted, unknown, number>({
    mutationFn: (elementId) => commitmentsApi.startIteration(elementId),
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/** Pulpit — jeden spójny stan pracy, jednym wywołaniem (spec sek. 8). */
export function useDashboard() {
  return useQuery<Dashboard>({
    queryKey: commitmentKeys.dashboard(),
    queryFn: ({ signal }) => commitmentsApi.fetchDashboard(signal),
  });
}

/**
 * „Przyjąłem do wiadomości" — z optimistic UI, bo to **jedyne miejsce**, gdzie się opłaca.
 *
 * Reszta akcji zmienia stan świata (budzi cudze zobowiązania, osierocia), więc front nie
 * ma jak przewidzieć wyniku. Tu zmiana jest lokalna i całkowicie przewidywalna: wiadomość
 * znika ze skrzynki. Czekanie na przelot przy klikaniu kolejnych pozycji byłoby wyłącznie
 * kosztem — a wycofanie przy odmowie przywraca dokładnie to, co było.
 */
export function useAcknowledgeNotice() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number, { previous: Dashboard | undefined }>({
    mutationFn: (noticeId) => commitmentsApi.acknowledgeNotice(noticeId),

    onMutate: async (noticeId) => {
      await queryClient.cancelQueries({ queryKey: commitmentKeys.dashboard() });

      const previous = queryClient.getQueryData<Dashboard>(commitmentKeys.dashboard());

      if (previous) {
        queryClient.setQueryData<Dashboard>(commitmentKeys.dashboard(), {
          ...previous,
          notices: previous.notices.filter((notice) => notice.id !== noticeId),
        });
      }

      return { previous };
    },

    onError: (_error, _noticeId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(commitmentKeys.dashboard(), context.previous);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: commitmentKeys.dashboard() });
    },
  });
}

/**
 * Obciążenie moich zespołów (decyzja 82).
 *
 * ⚠ Osobne zapytanie od pulpitu, mimo podobnych liczb — bo odpowiada na **inne pytanie**:
 * pulpit mówi „co jest na mnie", ten ekran „co jest na moich zespołach". Zlanie ich w jedno
 * zapytanie zmusiłoby serwer do zwracania sumy, z której front musiałby zgadywać podział.
 */
export function useTeamWorkload() {
  return useQuery<TeamWorkload[]>({
    queryKey: commitmentKeys.teamWorkload(),
    queryFn: ({ signal }) => commitmentsApi.fetchTeamWorkload(signal),
  });
}
